import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useContainerQueries } from "use-container-queries";

import {
  LoadingSpinner,
  Text,
  formGridBreakpoint,
} from "chameleon/lib/components";

import { ActiveFormFieldWrapper } from "checkout-experience/components/forms/active-form-field-wrapper";
import {
  checkoutFormGridStyle,
  checkoutFormSectionStyle,
  disclaimerStyle,
} from "checkout-experience/components/forms/form-section.css";
import { useActiveFormFields } from "checkout-experience/hooks/use-active-form-fields";
import { useFormFieldsLoading } from "checkout-experience/hooks/use-form-fields-loading";
import { usePaymentMethodMandate } from "checkout-experience/hooks/use-payment-method-mandate";
import { useHideSetAsDefault } from "checkout-experience/hooks/use-saved-payment-methods-list";
import { useSecurityCodeFieldBySettings } from "checkout-experience/hooks/use-security-code-field-by-settings";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";

import { MandateList } from "../mandate-list/mandate-list";
import { usePaymentOptionContext } from "../payment-method/payment-option-provider";

interface Props {
  editSavedPaymentMethod?: boolean;
  showActionSection?: boolean;
}

export const FormSection: FC<Props> = ({
  editSavedPaymentMethod,
  showActionSection = true,
}: Props) => {
  const { t } = useTranslation();
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "", paymentType } = paymentOption ?? {};
  const { checkoutSession, merchantSettings, checkoutSessionToken, target } =
    useChexContext();
  const hideSetAsDefault = useHideSetAsDefault();

  const { formHasPendingLoadingFields } = useFormFieldsLoading();

  // if delay spinner don't show it until loading is obvious to prevent "flapping"
  const [delaySpinner, setDelaySpinner] = useState<boolean>(true);

  const { data: mandate } = usePaymentMethodMandate(
    paymentType ?? PaymentType.creditCard
  );

  const { ref, active: breakpoint } = useContainerQueries({
    breakpoints: formGridBreakpoint,
    ignoreDimensions: true,
  });

  const isSavedPaymentMethod = formId !== paymentType?.toString();
  const { watch, register, setValue } = useFormContext();
  const { collectConsentToStoreForFuturePayments } = (watch(formId) ??
    {}) as ChexFieldValues;
  const securityCodeForPay = useSecurityCodeFieldBySettings({
    editSavedPaymentMethod,
    paymentOption,
    merchantSettings,
    checkoutSession,
  });

  const activeFields = useActiveFormFields({
    checkoutSession,
    merchantSettings,
    checkoutSessionToken,
    target,
    securityCodeForPay,
    paymentOption,
  });

  const hasSavedProfile = useMemo(
    () => Boolean(checkoutSession?.paymentMethods?.length),
    [checkoutSession?.paymentMethods?.length]
  );
  const setAsDefaultField = useMemo(
    () => activeFields.filter((field) => field.id === "setAsDefault")[0],
    [activeFields]
  );

  useEffect(() => {
    // set as default is not rendered in UI but only registered in form because
    // for a new user and new payment method, we want to set the value by default true
    // if save-for-future is set to true by user
    if (setAsDefaultField && !isSavedPaymentMethod && !hasSavedProfile) {
      register(`${formId}.setAsDefault`, {
        value: setAsDefaultField.initialValue,
      });
    }
  }, [setAsDefaultField, isSavedPaymentMethod, hasSavedProfile]);

  useEffect(() => {
    // if it's a new user and new payment method
    // then update value of setAsDefault based on collectConsentToStoreForFuturePayments
    if (!isSavedPaymentMethod && collectConsentToStoreForFuturePayments) {
      setValue(
        `${formId}.setAsDefault`,
        !hasSavedProfile ? collectConsentToStoreForFuturePayments : "false"
      );
    }
  }, [
    collectConsentToStoreForFuturePayments,
    hasSavedProfile,
    isSavedPaymentMethod,
  ]);

  useEffect(() => {
    // wait 750ms before showing the spinner if it is needed
    const timer = setTimeout(() => setDelaySpinner(false), 750);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {formHasPendingLoadingFields().length > 0 ? (
        <div
          style={{
            width: "100%",
            height: "146px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {!delaySpinner ? <LoadingSpinner /> : <></>}
        </div>
      ) : (
        <></>
      )}
      <div
        style={{
          display: formHasPendingLoadingFields().length > 0 ? "none" : "flex",
        }}
        // Ref to track container query
        // Ideally, would be on the radio button itself but for simplicity it is here
        ref={ref}
      >
        <div className={activeFields.length ? checkoutFormSectionStyle : ""}>
          {formId === PaymentType.giftCard.toString() ? (
            <div className={disclaimerStyle}>
              <Text
                styleVariant="body"
                tagElement="span"
                data-testid={"giftcard-disclaimer"}
              >
                {t("giftCard.disclaimer")}
              </Text>
            </div>
          ) : null}
          <div className={checkoutFormGridStyle}>
            {activeFields.map((field) => {
              return (
                <ActiveFormFieldWrapper
                  breakpoint={breakpoint}
                  field={field}
                  checkoutSession={checkoutSession}
                  hideSetAsDefault={hideSetAsDefault}
                  key={`${formId}-${field.id}`}
                  merchantSettings={merchantSettings}
                  checkoutSessionToken={checkoutSessionToken}
                  paymentOption={paymentOption}
                  editSavedPaymentMethod={editSavedPaymentMethod}
                  hasSavedProfile={hasSavedProfile}
                />
              );
            })}
          </div>
          {showActionSection && (
            <MandateList
              paymentType={paymentType}
              mandates={mandate ? [mandate] : []}
              placement={"paymentMethod"}
            />
          )}
        </div>
      </div>
    </>
  );
};
